import React, { Fragment } from 'react';
import Dotdotdot from 'react-dotdotdot';
import {MetricsElement} from 'react-metrics';

import { RadioIcon } from '../../../shared/components/radio';
import { withError } from '../../../shared/components/error-boundary';
import ImageWithPlaceholder from '../../../shared/components/image-with-placeholder';
import Tooltip from '../../../shared/components/tooltip';
import BundleContainer from './bundle-container';
import { ModalControl } from '../../../context/modal/modal-context';
import LearnMore from '../pdp-learn-more';
import { Helpers } from '../../../core/src/helpers';

import '../../css/carepack-module.less';

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

const getDefaultConfig = Helpers.memoize((sku, wrntyfeatures, ctoWarrantyChoices, customConfig) => {
    let ctoValue = typeof ctoWarrantyChoices === "object" ? ctoWarrantyChoices[0] : ctoWarrantyChoices;
    const defaultCarePackDuration = Helpers.getCarepackDuration(ctoValue || wrntyfeatures);
    let {
        defaultCarePack
    } = customConfig || {};
    defaultCarePack = defaultCarePack && Object.keys(defaultCarePack).length > 0 ?
        defaultCarePack :
        {
            name: defaultCarePackDuration ? `${defaultCarePackDuration}-Year Standard Warranty` : '1-Year Standard Warranty'
        };

    return {
        defaultCarePack,
        defaultHeaderText: 'Protect your Device with an HP Care Pack'
    };
})


class CarePackModule extends React.Component {

    constructor(props) {
        super(props);
        this.carePacks = Helpers.memoize((sku, carepackLength) => {
            return this.generateCarePackBundles();
        }, (...args) => (args[0] + args[1]));
    }

    generateCarePackBundles = () => {
        const { carePacks } = this.props;
        //transforming into bundle format
        const carePackBundles = carePacks.reduce((bundles, carePack, index) => {
            const bundle = {
                bundleIndex: index,
                metadata: {
                    disclaimer: false,
                    preselected: false,
                    icon: "",
                    /* display risk free if true, otherwise display 'HP Recommends' if last carepack or only carepack for more than 3 years*/
                    optionName: carePack.numYears > 2 ? (carePack.riskFree ?
                        'Risk Free' : index > 0 || carePacks.length === 1 ? 'HP Recommends' : null) : null,
                    valuemsg: carePack.name,
                },
                products: [carePack]
            };
            bundles.push(bundle);
            return bundles;
        }, []);
        return [carePacks, carePackBundles];
    }

    getMetaData = Helpers.memoize((defaultCarePackName, template) => {
        return {
            showPrice: true,
            template: template,
            preselect: true,
            checkbox: false,
            defaultOption: defaultCarePackName ? defaultCarePackName : "HP 1 year warranty"
        }
    });

    getHeaderComponent = Helpers.memoize((template, isSmb, sku, carePacks, carePackConfig) => {
        return (headerProps) => {
            return (
                <Fragment>
                    {template === 'default' && <ImageWithPlaceholder className='care-pack-logo' src={'https://www.hp.com/us-en/shop/app/assets/images/uploads/prod/carepack11595455803439805.png'} />}
                    <div className='care-pack-logo-text'>
                        {carePackConfig.defaultHeaderText ? carePackConfig.defaultHeaderText : 'Include an HP Care Pack'}
                        {!isSmb &&
                            <ModalControl
                                content={LearnMore}
                                contentProps={{
                                    sku,
                                    products: carePacks,
                                    customConfig: carePackConfig,
                                    ...headerProps
                                }}
                                modalProps={{ className: 'learn-more-outer' }}
                            >
                                {(props) => (
                                    <MetricsElement
                                        element='span'
                                        data-metrics-event-name='linkClick'
                                        data-metrics-event='e_linkClick'
                                        data-metrics-link-placement='product'
                                        data-metrics-link-id='carepack-learn-more'
                                    >
                                        <div className='pdp-learn-more' onClick={props.openModal}>
                                            Learn more
                                        </div>
                                    </MetricsElement>
                                )}
                            </ModalControl>
                        }
                    </div>
                </Fragment>
            )
        }
    }, (template, isSmb, sku, carePacks) => {
        return `${template}-${isSmb}-${sku}-${carePacks && carePacks.length}`;
    });

    render() {
        const { product, onChange, template = 'default', customConfig } = this.props;
        const { attributes, sku } = product;
        let carePackConfig = getDefaultConfig(sku, attributes.wrntyfeatures, attributes.choices_warranty, customConfig);
        const { defaultCarePack = EMPTY_OBJECT } = carePackConfig;
        const [carePacks, carePackBundles] = this.carePacks(product.sku, (this.props.carePacks || EMPTY_ARRAY).length);
        const isSmb = product.isSmb === "Yes" && this.props.carePacks.length < 1;

        return (
            <BundleContainer
                className='carepack-module'
                onChange={onChange}
                productSku={product.sku}
                productsList={carePacks}
                bundles={carePackBundles}
                metaData={this.getMetaData(defaultCarePack.name, template)}
                HeaderComponent={this.getHeaderComponent(template, isSmb, sku, carePacks, carePackConfig)}
                CustomItemComponent={CarePackItem}
                ariaLabel='Carepack Options'
                metricsId='carepack'
                enableMetrics
            />
        )
    }
}

const CarePackItem = ({ selected, onClick, showProductPrice, openModal, productPrice, template, bundle, className }) => {
    const renderContent = () => {
        switch (template) {
            case "pdp-puf":
                const violatorMsg =
                    <Dotdotdot clamp={1}>
                        <p className='content-header'>{valuemsg || optionName}</p>
                    </Dotdotdot>;

                return (
                    <Tooltip label={violatorMsg}>
                        <p>{valuemsg || optionName}</p>
                    </Tooltip>
                )
            default:
                return (
                    <Fragment>
                        <p className='content-promo'>{optionName}</p>
                        <p className='content-header'>{valuemsg}</p>
                    </Fragment>
                )
        }
    }
    const { metadata, bundleIndex } = bundle;
    const { optionName = '', valuemsg = '', icon = '', disclaimer } = metadata;
    const selectionPrice = className !== 'default-option' && showProductPrice ? productPrice : 'Included';
    const isRecommendHeader = optionName === 'HP Recommends';
    const selectionPriceAriaLabel = selectionPrice === 'Included' ? 'Included' : `$${Number(selectionPrice.props.children.props.price.salePrice).toFixed(2)}`;
    const itemSelected = selected ? ' selected' : '';
    return (
        <button
            aria-label={`${valuemsg || optionName} ${selectionPriceAriaLabel}${itemSelected}`}
            className={`bundle-item carepack-item${itemSelected}${isRecommendHeader ? ' hp-recommends' : ''}${className ? ` ${className}` : ''} unstyled`}
            onClick={() => onClick(bundleIndex)}
        >
            <div className='item-radio'>
                <RadioIcon />
            </div>
            <div className='item-radio-content'>
                <div className='care-pack-name'>
                    {renderContent()}
                </div>
                <div className='item-price'>
                    {selectionPrice}
                </div>
            </div>
        </button>
    )
}

export default withError(CarePackModule);
